import { DealListItemResponse } from 'app/console-api';
import { BodyText, FlexColumn, FlexRow, formatDate, SvgIcon } from '@make-software/cspr-design';
import { TaskShortPreview } from 'app/components/management/apps/deals/view/task-short-preview';
import { AppShortPreview } from 'app/components/management/apps/deals/view/app-short-preview';
import DetailsNameTableData from 'app/components/management/common/details-name-table-data/details-name-table-data';
import DetailsValueTableData from 'app/components/management/common/details-value-table-data/details-value-table-data';
import DetailsTableRow from 'app/components/management/common/details-table-row/details-table-row';
import React from 'react';
import InfoIcon from 'assets/icons/ic-info.svg';
import {
  EditButton,
  StyledChipsTag,
  StyledLink,
  StyledTooltip,
  ViewPageContainer
} from 'app/components/common/common-styles';
import { MyDealsStatus } from 'app/components/common/deals-statuses/my-deals-status';
import { APPS_DEALS_EDIT_PATH, APPS_DEALS_VIEW_LOGS_PATH } from 'app/route/paths';
import styled from 'styled-components';
import { DealAutomationRules } from 'app/components/management/apps/deals/view/deal-automation-rules';
import { ProgressBar } from 'app/components/management/apps/deals/common/progress-bar';
import { EqualTrafficConfig } from 'app/config';
import { generatePath, useNavigate } from 'react-router-dom';
import { DealStatus } from 'app/components/common/deals-statuses/deals-statuses';
import { RewardsViewBlock } from 'app/components/management/common/rewards/rewards-view';
import { RewardsType } from 'app/console-api/apps/types';
import {DealTradeChip} from 'app/components/common/deal-trade-type/deal-trade-chip';
import {DealViewOfferStructure} from 'app/components/management/apps/deals/view/deal-view-offer-structure';

const StyledSvgIcon = styled(SvgIcon)`
  margin-left: 5px;
`;

const StyledSubtitleText = styled.div`
  font-size: 18px;
  font-weight: bold;
`;

const StyledText = styled.div`
  text-transform: none;
`;

const StyledViewRow = styled(FlexRow)(({ theme }) =>
  theme.withMedia({
    marginLeft: '20px',
    flexWrap: ['wrap', 'wrap', 'unset']
  })
);

const StyledPreviewColumn = styled(FlexColumn)(({ theme }) =>
  theme.withMedia({
    minWidth: '240px'
  })
);

export interface DealViewDetailsProps {
  data: DealListItemResponse;
  rewardsTypes: RewardsType[];
}

export const DealViewDetails = ({ data, rewardsTypes }: DealViewDetailsProps) => {
  const navigate = useNavigate();
  const onEdit = () => {
    navigate(
      generatePath(APPS_DEALS_EDIT_PATH, {
        appID: data.myApp.id,
        id: data.dealDetails.id
      })
    );
  };

  return (
    <ViewPageContainer gap={24}>
      <StyledViewRow>
        <StyledSubtitleText>Details</StyledSubtitleText>
      </StyledViewRow>
      <DealViewOfferStructure data={data} />
      <FlexColumn>
        <DetailsTableRow>
          <DetailsNameTableData>Title</DetailsNameTableData>
          <DetailsValueTableData>
            <BodyText size={3}>{data.dealDetails.title}</BodyText>
          </DetailsValueTableData>
        </DetailsTableRow>
        <DetailsTableRow>
          <DetailsNameTableData>Status</DetailsNameTableData>
          <DetailsValueTableData>
            <MyDealsStatus
              status={data.dealStatus}
              type={data.dealDetails.requestType}
              statusDescription={data.statusDescription}
            />
          </DetailsValueTableData>
        </DetailsTableRow>
        {[DealStatus.Active, DealStatus.Cancelled, DealStatus.Suspended].includes(
          data.dealStatus
        ) && (
          <DetailsTableRow>
            <DetailsNameTableData>Statistics</DetailsNameTableData>
            <DetailsValueTableData>
              <ProgressBar stats={data.stats} />
            </DetailsValueTableData>
          </DetailsTableRow>
        )}
        {[DealStatus.Suspended, DealStatus.Rejected].includes(data.dealStatus) && (
          <DetailsTableRow>
            <DetailsNameTableData>Status Description</DetailsNameTableData>
            <DetailsValueTableData>{data.statusDescription}</DetailsValueTableData>
          </DetailsTableRow>
        )}
        <DetailsTableRow>
          <DetailsNameTableData>Deal type</DetailsNameTableData>
          <DetailsValueTableData>
          <DealTradeChip type={data.dealDetails.tradeType}></DealTradeChip>
          </DetailsValueTableData>
        </DetailsTableRow>
        {data.categories && (
          <DetailsTableRow>
            <DetailsNameTableData>Categories</DetailsNameTableData>
            <DetailsValueTableData>
              <FlexRow wrap={'wrap'} gap={8}>
                {data.categories.map((cat, idx) => (
                  <StyledChipsTag key={idx}>{cat}</StyledChipsTag>
                ))}
              </FlexRow>
            </DetailsValueTableData>
          </DetailsTableRow>
        )}
        <DetailsTableRow>
          <DetailsNameTableData>Rewards</DetailsNameTableData>
          <DetailsValueTableData>
            <FlexRow wrap={'wrap'} gap={8}>
              <RewardsViewBlock
                rewardValues={data.rewards}
                rewardTypes={rewardsTypes}
              ></RewardsViewBlock>
            </FlexRow>
          </DetailsValueTableData>
        </DetailsTableRow>
        <DetailsTableRow>
          <DetailsNameTableData>Created At</DetailsNameTableData>
          <DetailsValueTableData>
            {formatDate(data.dealDetails.createdAt.toLocaleString())}
          </DetailsValueTableData>
        </DetailsTableRow>
        <DetailsTableRow>
          <DetailsNameTableData>Updated At</DetailsNameTableData>
          <DetailsValueTableData>
            {formatDate(data.dealDetails.updatedAt.toLocaleString())}
          </DetailsValueTableData>
        </DetailsTableRow>
        <DetailsTableRow>
          <DetailsNameTableData>
            <StyledLink
              to={generatePath(APPS_DEALS_VIEW_LOGS_PATH, {
                appID: data.myApp.id,
                id: data.dealDetails.id
              })}
            >
              <StyledText>See all action logs</StyledText>
            </StyledLink>
          </DetailsNameTableData>
        </DetailsTableRow>
      </FlexColumn>
      <StyledViewRow>
        <DealAutomationRules
          EqualTrafficConfig={EqualTrafficConfig}
          automations={data.automations}
          tradeType={data.dealDetails.tradeType}
        />
      </StyledViewRow>
      {(data.dealStatus === DealStatus.Active ||
        data.dealStatus === DealStatus.Pending ||
        data.dealStatus === DealStatus.NotStarted ||
        data.dealStatus === DealStatus.Suspended) && (
        <StyledViewRow>
          <EditButton onClick={onEdit}>Edit</EditButton>
        </StyledViewRow>
      )}
    </ViewPageContainer>
  );
};
